import { useMemo, useState } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  LinearProgress,
  ListButton,
  Pagination,
  ReferenceManyField,
  Show,
  ShowButton,
  TextField,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import { Navigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isNativeCampaignId, useNativeCampaign } from 'src/client/components/forms/hooks/useNativeCampaign';

import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { AddCampaignLabelButton } from 'src/client/components/exclusion-labels/add-campaign-label-button';
import {
  CampaignExclusionLabelChips,
  LineItemExclusionLabelChips,
} from 'src/client/components/exclusion-labels/exclusion-label-chips';
import { includesInsensitive } from 'src/client/components/forms/form-utils';
import { SharePointUrlField, formatCampaignTitle, getRelatedName } from './CampaignList';
import { formatLineItemTitle } from '../line-items/LineItemCreate';
import { DuplicateLineItemButton } from '../line-items/DuplicateLineItemButton';
import { LineItemArchiveButton } from '../line-items/LineItemArchiveButton';
import { CampaignArchiveButton } from './CampaignArchiveButton';

type CampaignRecord = {
  archivedAt?: string | null;
  id?: string;
  externalPioId?: string | null;
  advertiserId?: string | null;
  agencyId?: string | null;
  brandId?: string | null;
  billToAdvertiserId?: string | null;
  campaignNumber?: number | null;
  name?: string | null;
};

type LineItemRecord = {
  id?: string;
  externalPioId?: string | null;
  lineItemNumber?: number | null;
  name?: string | null;
  product?: { name?: string | null } | null;
  productByProductId?: { name?: string | null } | null;
  productId?: string | null;
};

const showValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const HeaderValue = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Box>
);

const CampaignShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <CampaignArchiveButton />
  </TopToolbar>
);

const CampaignHeader = () => {
  const record = useRecordContext<CampaignRecord & Record<string, any>>();

  if (!record) return null;

  return (
    <CardContent>
      <CardHeader title={formatCampaignTitle(record)} sx={{ px: 0, pt: 0 }} />
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap={2}>
        <HeaderValue label="Advertiser">
          {showValue(getRelatedName(record, ['advertiser', 'advertiserByAdvertiserId'], 'advertiserId'))}
        </HeaderValue>
        <HeaderValue label="Agency">
          {showValue(getRelatedName(record, ['agency', 'advertiserByAgencyId'], 'agencyId'))}
        </HeaderValue>
        <HeaderValue label="Brand">
          {showValue(getRelatedName(record, ['brand', 'advertiserByBrandId'], 'brandId'))}
        </HeaderValue>
        <HeaderValue label="Bill-To Advertiser">
          {showValue(
            getRelatedName(record, ['billToAdvertiser', 'advertiserByBillToAdvertiserId'], 'billToAdvertiserId'),
          )}
        </HeaderValue>
        <HeaderValue label="Start Date">
          <ESTDateField record={record} source="startDate" />
        </HeaderValue>
        <HeaderValue label="End Date">
          <ESTDateField record={record} source="endDate" />
        </HeaderValue>
        <HeaderValue label="Status">{record.archivedAt ? 'Archived' : 'Active'}</HeaderValue>
        <HeaderValue label="SharePoint URL">
          <SharePointUrlField />
        </HeaderValue>
        <HeaderValue label="GAM Order ID">{showValue(record.gamOrderId)}</HeaderValue>
        <HeaderValue label="DCM Advertiser ID">{showValue(record.dcmAdvertiserId)}</HeaderValue>
        <HeaderValue label="Total Contract Value">{showValue(record.budget)}</HeaderValue>
        <HeaderValue label="PO Number">{showValue(record.poNumber)}</HeaderValue>
        <HeaderValue label="Trafficker">{showValue(record.traffickerEmail)}</HeaderValue>
        <HeaderValue label="Salesperson">{showValue(record.salespersonEmail)}</HeaderValue>
        <HeaderValue label="Archived At">
          {record.archivedAt ? <ESTDateField record={record} source="archivedAt" showTime /> : '-'}
        </HeaderValue>
      </Box>
    </CardContent>
  );
};

/** Campaign-level exclusion labels (MD-5188). Mirrors legacy CampaignShowLayout attach UI. */
const CampaignExclusionLabelsSection = () => {
  const record = useRecordContext<CampaignRecord>();

  if (!record) return null;

  return (
    <CardContent>
      <CardHeader title="Campaign Exclusion Labels" sx={{ px: 0, pt: 0 }} />
      <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.625}>
        <CampaignExclusionLabelChips record={record} deletionEnabled />
        <AddCampaignLabelButton />
      </Box>
    </CardContent>
  );
};

const LineItemsToolbar = ({
  filterValue,
  onFilterChange,
  includeArchived,
  onIncludeArchivedChange,
}: {
  filterValue: string;
  onFilterChange: (value: string) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (value: boolean) => void;
}) => {
  const record = useRecordContext<CampaignRecord>();

  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <MuiTextField
          label="Filter line items"
          value={filterValue}
          onChange={event => onFilterChange(event.target.value)}
          size="small"
          sx={{ minWidth: 260 }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={includeArchived}
              onChange={event => onIncludeArchivedChange(event.target.checked)}
              size="small"
            />
          }
          label="Include Archived"
        />
      </Box>
      <CreateButton
        resource="lineItems"
        label="Add line item"
        to={{
          pathname: '/lineItems/create',
          search: record?.id ? `?campaignId=${record.id}` : undefined,
        }}
      />
    </Box>
  );
};

const LineItemsTable = () => {
  const [filterValue, setFilterValue] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const lineItemFilter = useMemo(
    () => ({
      ...(filterValue ? { name: includesInsensitive.parse(filterValue) } : {}),
      ...(includeArchived ? { includeArchived: true } : {}),
    }),
    [filterValue, includeArchived],
  );

  return (
    <CardContent>
      <CardHeader title="Line Items" sx={{ px: 0, pt: 0 }} />
      <LineItemsToolbar
        filterValue={filterValue}
        onFilterChange={setFilterValue}
        includeArchived={includeArchived}
        onIncludeArchivedChange={setIncludeArchived}
      />
      <ReferenceManyField
        reference="lineItems"
        target="campaignId"
        sort={{ field: 'startDate', order: 'DESC' }}
        perPage={50}
        filter={lineItemFilter}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
      >
        <Datagrid rowClick="show" bulkActionButtons={false}>
          <FunctionField
            label="Line Item"
            source="name"
            render={(record: LineItemRecord) => formatLineItemTitle(record)}
          />
          <ESTDateField source="startDate" label="Start Date" />
          <ESTDateField source="endDate" label="End Date" />
          <FunctionField
            source="productId"
            label="Product"
            render={(record: LineItemRecord) => getRelatedName(record, ['product', 'productByProductId'], 'productId')}
          />
          <TextField source="status" label="Status" />
          <TextField source="contractedUnits" label="Contracted Units" emptyText="-" />
          <TextField source="costBasis" label="Cost Basis" />
          <TextField source="invoiceAmount" label="Invoice Amount" />
          <TextField source="revenue" label="Revenue" />
          <TextField source="gamLineItemId" label="GAM Line Item ID" />
          <FunctionField
            label="Exclusion Labels"
            render={(record: LineItemRecord) => <LineItemExclusionLabelChips record={record} />}
          />
          <ShowButton resource="lineItems" />
          <EditButton resource="lineItems" />
          <DuplicateLineItemButton />
          <LineItemArchiveButton />
        </Datagrid>
      </ReferenceManyField>
    </CardContent>
  );
};

export const CampaignShow = () => {
  const { id } = useParams();
  const needsLegacyResolve = Boolean(id && !isNativeCampaignId(id));
  // MD-5186: numeric PIO campaign ids used to bounce to dead placementCampaigns.
  // Resolve via campaigns.externalPioId and land on native UUID show instead.
  const { campaign, isLoading, error } = useNativeCampaign(needsLegacyResolve ? id : undefined);

  if (needsLegacyResolve) {
    if (isLoading) return <LinearProgress />;
    if (campaign?.id) {
      return <Navigate to={`/campaigns/${encodeURIComponent(String(campaign.id))}/show`} replace />;
    }
    const errorMessage = error instanceof Error ? error.message : undefined;
    return (
      <CardContent>
        <Typography color="error">{errorMessage ?? `Campaign not found for id ${id}`}</Typography>
      </CardContent>
    );
  }

  return (
    <Show actions={<CampaignShowActions />}>
      <CampaignHeader />
      <Divider />
      <CampaignExclusionLabelsSection />
      <Divider />
      <LineItemsTable />
    </Show>
  );
};
